import api from './api'

/**
 * Types pour les abonnements
 */

export interface Plan {
  id: string
  name: string
  price: number
  currency: string
  interval: string
  features: string[]
}

export interface Subscription {
  id: number | string
  plan: string
  planName?: string | null
  description?: string
  status: string
  amount: string | number
  currency: string
  interval: string
  currentPeriodStart: string | null
  currentPeriodEnd: string | null
  canceledAt: string | null
  createdAt: string
  stripeSubscriptionId?: string
  productId?: string
  priceId?: string
  accessLevel?: 'basic' | 'premium' | 'vip' | null
  isTrial?: boolean
  trialEndsAt?: string | null
}

export interface MySubscriptionsResponse {
  subscriptions: Subscription[]
  hasStripeCustomer: boolean
  hasUsedTrial: boolean
}

export interface CheckoutSessionResponse {
  sessionId: string
  url: string
}

export interface CheckoutRequest {
  planId: string
}

export interface CancelSubscriptionResponse {
  message: string
}

export interface Invoice {
  id: string
  number: string | null
  status: string | null
  amount_due: number
  amount_paid: number
  currency: string
  description: string | null
  created: string | null
  period_start: string | null
  period_end: string | null
  hosted_invoice_url: string | null
  invoice_pdf: string | null
  subscription_id: string | null
}

export interface MyInvoicesResponse {
  invoices: Invoice[]
}

/**
 * Service pour gérer les abonnements Stripe
 */

class SubscriptionService {
  private readonly basePath = '/subscriptions'

  /**
   * Récupère la liste des plans d'abonnement disponibles
   * @returns Promise<Plan[]>
   */
  async getPlans(): Promise<Plan[]> {
    try {
      const response = await api.get<Plan[]>(`${this.basePath}/plans`)
      return response.data
    } catch (error) {
      console.error('Erreur lors de la récupération des plans:', error)
      throw error
    }
  }

  /**
   * Crée une session de checkout Stripe pour un abonnement
   * @param planId - ID du plan (basic, premium, vip)
   * @returns Promise<CheckoutSessionResponse>
   */
  async createCheckoutSession(planId: string): Promise<CheckoutSessionResponse> {
    try {
      const response = await api.post<CheckoutSessionResponse>(
        `${this.basePath}/checkout`,
        { planId }
      )
      return response.data
    } catch (error) {
      console.error('Erreur lors de la création de la session de checkout:', error)
      throw error
    }
  }

  /**
   * Récupère les abonnements de l'utilisateur connecté
   * @returns Promise<Subscription[]>
   */
  async getMySubscriptions(): Promise<MySubscriptionsResponse> {
    try {
      const response = await api.get<MySubscriptionsResponse>(`${this.basePath}/my-subscriptions`)
      return {
        subscriptions: response.data.subscriptions,
        hasStripeCustomer: response.data.hasStripeCustomer,
        hasUsedTrial: response.data.hasUsedTrial ?? false,
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des abonnements:', error)
      throw error
    }
  }

  /**
   * Récupère les factures de l'utilisateur connecté
   * @returns Promise<Invoice[]>
   */
  async getMyInvoices(): Promise<Invoice[]> {
    try {
      const response = await api.get<MyInvoicesResponse>(`${this.basePath}/my-invoices`)
      return response.data.invoices
    } catch (error) {
      console.error('Erreur lors de la récupération des factures:', error)
      throw error
    }
  }

  /**
   * Annule un abonnement
   * @param subscriptionId - ID de l'abonnement à annuler
   * @returns Promise<CancelSubscriptionResponse>
   */
  async cancelSubscription(subscriptionId: number): Promise<CancelSubscriptionResponse> {
    try {
      const response = await api.post<CancelSubscriptionResponse>(
        `${this.basePath}/${subscriptionId}/cancel`
      )
      return response.data
    } catch (error) {
      console.error('Erreur lors de l\'annulation de l\'abonnement:', error)
      throw error
    }
  }

  /**
   * Vérifie si l'utilisateur a un abonnement actif pour un plan donné
   * @param plan - Nom du plan (BASIC, PREMIUM, VIP)
   * @returns Promise<boolean>
   */
  async hasActivePlan(plan: string): Promise<boolean> {
    try {
      const { subscriptions } = await this.getMySubscriptions()
      return subscriptions.some(
        sub => sub.plan === plan && sub.status === 'active'
      )
    } catch (error) {
      console.error('Erreur lors de la vérification du plan actif:', error)
      return false
    }
  }

  /**
   * Récupère l'abonnement actif de l'utilisateur (s'il existe)
   * @returns Promise<Subscription | null>
   */
  async getActiveSubscription(): Promise<Subscription | null> {
    try {
      const { subscriptions } = await this.getMySubscriptions()
      const activeSubscription = subscriptions.find(
        sub => sub.status === 'active'
      )
      return activeSubscription || null
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'abonnement actif:', error)
      return null
    }
  }

  /**
   * Récupère le niveau d'accès actif de l'utilisateur
   * @returns Promise<'basic' | 'premium' | 'vip' | null>
   */
  async getActiveAccessLevel(): Promise<'basic' | 'premium' | 'vip' | null> {
    try {
      const { subscriptions } = await this.getMySubscriptions()
      const activeSubscription = subscriptions.find(
        sub => sub.status === 'active'
      )
      return activeSubscription?.accessLevel || null
    } catch (error) {
      console.error('Erreur lors de la récupération du niveau d\'accès:', error)
      return null
    }
  }

  /**
   * Vérifie si l'utilisateur a accès au niveau donné ou supérieur
   * @param requiredLevel - Niveau d'accès requis ('basic', 'premium', 'vip')
   * @returns Promise<boolean>
   */
  async hasAccessLevel(requiredLevel: 'basic' | 'premium' | 'vip'): Promise<boolean> {
    try {
      const accessLevel = await this.getActiveAccessLevel()
      if (!accessLevel) return false
      
      const levels = { basic: 1, premium: 2, vip: 3 }
      const userLevel = levels[accessLevel] || 0
      const requiredLevelValue = levels[requiredLevel] || 0
      
      return userLevel >= requiredLevelValue
    } catch (error) {
      console.error('Erreur lors de la vérification du niveau d\'accès:', error)
      return false
    }
  }

  /**
   * Redirige l'utilisateur vers la page de checkout Stripe
   * @param planId - ID du plan à souscrire
   */
  async redirectToCheckout(planId: string): Promise<void> {
    try {
      const { url } = await this.createCheckoutSession(planId)
      window.location.href = url
    } catch (error) {
      console.error('Erreur lors de la redirection vers le checkout:', error)
      throw error
    }
  }
}

// Export d'une instance unique du service
export const subscriptionService = new SubscriptionService()

export default subscriptionService


